/**
 * The overnight society tick (CLAUDE.md mechanic #1 + idle/offline rule #8).
 *
 * `runOvernightTick(input)` advances the simulated society forward through the
 * elapsed (soft-capped) in-game days while the player was away, then emits the
 * ground-truth `Beat[]` the voice layer phrases into a warm welcome-back ("Maple
 * evolved her Eevee," "your guild finished its goal"). It is a PURE function of
 * `worldSeed + toDate + elapsedDays + state`: identical inputs ⇒ byte-identical
 * output (heavily unit-tested), and it never mutates its input.
 *
 * Warm by construction: trainers level/catch/evolve, befriend each other and the
 * player, and stage only *gentle, NPC-only* rivalry. No beat ever aims negativity
 * at the player; player-facing affinity is floored at zero.
 *
 * Soft cap: a months-long absence collapses to MAX_TICK_DAYS of advancement, so
 * returning is always a lovely welcome-back and never a runaway backlog.
 *
 * Species data is injected (defaulting to the hermetic fixtures) so the tick stays
 * pure; production wires the generated dataset via {@link makeOvernightTick}.
 */
import type {
  SpeciesData,
  SimTrainer,
  SocietyTickFn,
  SocietyTickInput,
  SocietyTickOutput,
  Beat,
  BeatKind,
  BeatPriority,
  ChatChannel,
  Relationship,
  MemoryRecord,
  PokemonInstance,
  Nature,
} from '@/shared';
import { makeRng, hash, hashString, type Rng } from '@/core';
import { getNature } from '../core/stats.js';
import { makeLevelUpEvents, expForLevel } from '../core/leveling.js';
import { checkEvolution, evolveInstance } from '../core/evolution.js';
import { FIXTURE_SPECIES } from '@/data';
import { makeInstance, EPOCH } from './roster.js';

/** Soft cap on simulated days — a long absence is a lovely welcome-back (D: idle). */
export const MAX_TICK_DAYS = 5;

/** Cap on retained memories per trainer (oldest pruned) — keeps state bounded. */
const MEMORY_CAP = 24;

// ── Conversational texture banks (D14) ───────────────────────────────────────
//
// These supply *ground-truth subject matter* — never finished prose. The voice
// layer phrases them; here they only guarantee that the emitted Beat[] carries
// RICH, varied, two-way material to talk about (peacocking, mutual admiration,
// lore/know-how as culture, little ongoing storylines) instead of repeated
// greetings. Warm by construction: nothing here is one-upmanship or aimed at the
// player. All picks are deterministic (drawn from a seeded Rng).

/** Why a trainer is fondly proud of one of their mons (peacocking flavor). */
const PRIDE_REASONS: readonly string[] = [
  'finally evolved it',
  'raised it from an egg',
  'its IVs came out gorgeous',
  'nailed the nature this time',
  'it has been with me since day one',
  'it just learned a move I adore',
  'the friendship is through the roof',
  'caught it on a perfect morning',
];

/** What one trainer warmly admires about another's (or the player's) mon. */
const ADMIRE_REASONS: readonly string[] = [
  'what a gorgeous coat',
  'that is a brilliant moveset',
  'love the nickname',
  'those are dreamy stats',
  'such a friendly little thing',
  'you have raised it so well',
  'I have always wanted one of those',
  'the way it carries itself, wow',
];

/** Two-way prompts: an NPC asking the player about their team (genuinely curious). */
const TWO_WAY_QUESTIONS: readonly string[] = [
  'how did you train it up so fast',
  'where did you find yours',
  'what nature did you go for',
  'is it as sweet as it looks',
  'what moves are you running on it',
  'mind if I see it sometime',
];

/**
 * Lore / know-how shared as community culture — tips a regular would happily
 * pass on. Not quests, not gates: ambient, optional, warm.
 */
const KNOWHOW_TIPS: readonly string[] = [
  'rare grass-types love the deep forest at dawn',
  'try fishing the lakeshore on rainy days for the shy ones',
  'a gentle nature keeps a defensive mon happier',
  'leave an egg at the day-camp and it hatches with inherited moves',
  'the ruins hide species you will not see anywhere else',
  'high friendship can trigger some of the prettiest evolutions',
  'matching egg groups is the trick to passing on a nature',
  'the mountain slope is where the brave bug-types hang out',
];

/** Whispered, never-missable legendary rumors — lore as a shared campfire story. */
const LEGEND_RUMORS: readonly string[] = [
  'they say something powerful drifts through the old ruins at night',
  'a traveler swears they saw a legendary skim the lake at dusk',
  'old-timers tell of a roamer that never visits the same biome twice',
  'there is a tale of a guardian sleeping deep in the cave',
];

/** Gentle, NPC-only storylines that recur — the world having its own quiet life. */
const STORYLINES: readonly string[] = [
  'rivalry-softening', // two old rivals slowly becoming friends
  'breeder-iv-project', // a breeder chasing a perfect-IV line
  'festival-planning', // regulars organizing a town get-together
  'dex-milestone', // a collector closing in on a dex goal
  'mentor-coaching', // a veteran helping a newcomer level up
];

/** Topics for ambient peacocking/admiration/culture chitchat (live + overnight). */
type ChatTopic = 'show-off' | 'admire' | 'know-how' | 'storyline' | 'pottering';

const FRIENDSHIP_MAX = 100;
const FRIENDSHIP_MIN = 0;
const AFFINITY_MAX = 100;
const AFFINITY_MIN = -100;

interface SpeciesIndex {
  get(id: string): SpeciesData | undefined;
  all(): readonly SpeciesData[];
}

function indexSpecies(species: readonly SpeciesData[]): SpeciesIndex {
  const byId = new Map(species.map((s) => [s.id, s]));
  return { get: (id) => byId.get(id), all: () => species };
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/** Deep, JSON-faithful clone so we never mutate the caller's input trainers. */
function cloneTrainers(trainers: readonly SimTrainer[]): SimTrainer[] {
  return trainers.map((t) => ({
    ...t,
    personality: { ...t.personality, traits: [...t.personality.traits] },
    team: t.team.map((m) => ({
      ...m,
      ivs: { ...m.ivs },
      evs: { ...m.evs },
      moves: m.moves.map((mv) => ({ ...mv })),
      originalTrainer: { ...m.originalTrainer },
    })),
    relationships: t.relationships.map((r) => ({ ...r })),
    memories: t.memories.map((mem) => ({ ...mem })),
  }));
}

// ── Beat construction ────────────────────────────────────────────────────────

interface BeatDraft {
  readonly kind: BeatKind;
  readonly priority: BeatPriority;
  readonly subjectTrainerId: string;
  readonly involvedTrainerIds?: readonly string[];
  readonly channel: ChatChannel;
  readonly data: Record<string, string | number | boolean>;
}

/** A small ordered collector that assigns stable ids + a non-decreasing clock. */
class BeatLog {
  private readonly beats: Beat[] = [];
  private seq = 0;
  constructor(private readonly baseTime: number) {}

  push(draft: BeatDraft): void {
    const at = this.baseTime + this.seq;
    this.beats.push({
      id: `beat-${this.seq}`,
      kind: draft.kind,
      priority: draft.priority,
      subjectTrainerId: draft.subjectTrainerId,
      ...(draft.involvedTrainerIds ? { involvedTrainerIds: draft.involvedTrainerIds } : {}),
      channel: draft.channel,
      data: draft.data,
      createdAt: at,
    });
    this.seq++;
  }

  all(): readonly Beat[] {
    return this.beats;
  }
}

// ── Memory ───────────────────────────────────────────────────────────────────

function remember(t: SimTrainer, rec: Omit<MemoryRecord, 'id'>, key: string): void {
  const mem: MemoryRecord = { id: `mem-${key}`, ...rec };
  t.memories.push(mem);
  if (t.memories.length > MEMORY_CAP) {
    t.memories.splice(0, t.memories.length - MEMORY_CAP);
  }
}

function adjustRelationship(
  t: SimTrainer,
  otherId: string,
  delta: number,
  kindIfNew: string,
): void {
  let rel: Relationship | undefined = t.relationships.find((r) => r.otherId === otherId);
  if (!rel) {
    rel = { otherId, affinity: 0, kind: kindIfNew };
    t.relationships.push(rel);
  }
  const floor = otherId === 'player' ? 0 : AFFINITY_MIN; // player-facing never goes negative
  rel.affinity = clamp(rel.affinity + delta, floor, AFFINITY_MAX);
}

// ── Per-trainer daily advancement ──────────────────────────────────────────────

/** Advance one mon's level by a gentle daily amount; returns true if it grew. */
function dayLevelGain(
  mon: PokemonInstance,
  species: SpeciesData,
  nature: Nature,
  rng: Rng,
): { leveled: boolean; toLevel: number } {
  if (mon.level >= 100) return { leveled: false, toLevel: mon.level };
  // A gentle nudge: most days a trainer trains a little. Generous but bounded so a
  // 5-day tick advances a few levels, never explosively.
  if (!rng.chance(0.6)) return { leveled: false, toLevel: mon.level };
  const targetLevel = Math.min(100, mon.level + 1 + rng.int(2)); // +1..+2
  const newExp = expForLevel(species.growthRate, targetLevel);
  const before = mon.level;
  makeLevelUpEvents(mon, newExp, species, nature);
  return { leveled: mon.level > before, toLevel: mon.level };
}

/** Try to evolve a mon after growth (level-up + friendship triggers only). */
function tryEvolve(
  mon: PokemonInstance,
  species: SpeciesData,
  speciesIdx: SpeciesIndex,
): { evolved: PokemonInstance; toSpeciesId: string } | null {
  for (const trigger of ['level-up', 'friendship'] as const) {
    const match = checkEvolution(mon, species, trigger);
    if (match) {
      const toSpecies = speciesIdx.get(match.toSpeciesId);
      if (toSpecies) {
        return { evolved: evolveInstance(mon, toSpecies), toSpeciesId: match.toSpeciesId };
      }
    }
  }
  return null;
}

interface DayContext {
  readonly rng: Rng;
  readonly speciesIdx: SpeciesIndex;
  readonly log: BeatLog;
  readonly trainers: readonly SimTrainer[];
  readonly dayIndex: number;
  readonly toDate: string;
}

function channelFor(t: SimTrainer): ChatChannel {
  return t.guildId ? 'guild' : t.lod === 'regular' ? 'zone' : 'global';
}

/** The mon a trainer is fondest of right now (highest level; ties → first). */
function proudestMon(t: SimTrainer): PokemonInstance | undefined {
  if (t.team.length === 0) return undefined;
  return t.team.reduce((best, m) => (m.level > best.level ? m : best), t.team[0]!);
}

/**
 * Peacocking beat: a trainer warmly shows off one of their team members. Carries
 * the proud species + a fond reason so the voice has real material (not "hi").
 * Two-way friendly: it invites others to admire, never to compare downward.
 */
function pushShowOff(t: SimTrainer, rng: Rng, ctx: DayContext, channel: ChatChannel): void {
  const star = proudestMon(t);
  if (!star) return;
  ctx.log.push({
    kind: 'chitchat',
    priority: 'ambient',
    subjectTrainerId: t.id,
    channel,
    data: {
      topic: 'show-off',
      speciesId: star.speciesId,
      level: star.level,
      ...(star.nickname ? { nickname: star.nickname } : {}),
      pride: rng.pick(PRIDE_REASONS),
      warm: true,
    },
  });
}

/**
 * Admiration beat: one trainer warmly compliments a peer's mon (mutual flattery,
 * fond bragging). Strictly upward — admiration, never one-upmanship.
 */
function pushAdmire(
  t: SimTrainer,
  other: SimTrainer,
  rng: Rng,
  ctx: DayContext,
  channel: ChatChannel,
): void {
  const theirStar = proudestMon(other);
  if (!theirStar) return;
  ctx.log.push({
    kind: 'chitchat',
    priority: 'ambient',
    subjectTrainerId: t.id,
    involvedTrainerIds: [other.id],
    channel,
    data: {
      topic: 'admire',
      aboutTrainerId: other.id,
      aboutSpeciesId: theirStar.speciesId,
      compliment: rng.pick(ADMIRE_REASONS),
      warm: true,
    },
  });
}

/**
 * Lore / know-how beat: a regular shares a tip or a never-missable legend rumor
 * as community culture. `lore-rumor` for the campfire legends; `chitchat` for the
 * practical tips. Ambient + optional, never a quest.
 */
function pushKnowHow(t: SimTrainer, rng: Rng, ctx: DayContext, channel: ChatChannel): void {
  if (rng.chance(0.35)) {
    ctx.log.push({
      kind: 'lore-rumor',
      priority: 'normal',
      subjectTrainerId: t.id,
      channel,
      data: { topic: 'rumor', rumor: rng.pick(LEGEND_RUMORS), warm: true },
    });
  } else {
    ctx.log.push({
      kind: 'chitchat',
      priority: 'ambient',
      subjectTrainerId: t.id,
      channel,
      data: { topic: 'know-how', tip: rng.pick(KNOWHOW_TIPS), warm: true },
    });
  }
}

/**
 * Storyline beat: a gentle ongoing thread the world carries on its own — a
 * softening rivalry, a breeder's IV project, festival planning. NPC-only drama,
 * always mild and warm; rivalries here are *softening*, never bitter.
 */
function pushStoryline(t: SimTrainer, rng: Rng, ctx: DayContext, channel: ChatChannel): void {
  const story = rng.pick(STORYLINES);
  // A rivalry storyline references a real peer it's softening *with*.
  let withId: string | undefined;
  if (story === 'rivalry-softening' || story === 'mentor-coaching') {
    const rivalRel = t.relationships.find(
      (r) => r.otherId !== 'player' && (story === 'rivalry-softening' ? r.kind === 'rival' : true),
    );
    withId = rivalRel?.otherId;
  }
  ctx.log.push({
    kind: 'chitchat',
    priority: 'ambient',
    subjectTrainerId: t.id,
    ...(withId ? { involvedTrainerIds: [withId] } : {}),
    channel,
    data: {
      topic: 'storyline',
      storyline: story,
      ...(withId ? { withTrainerId: withId } : {}),
      warm: true,
    },
  });
}

/**
 * Pick what a trainer chats about, weighted by play style so the world's voices
 * differ (a breeder shares know-how, a battler shows off, a socializer admires).
 * Deterministic from the seeded Rng. Excludes the bland 'pottering' fallback —
 * that is only used by the live ambient layer when nothing richer applies.
 */
function pickConversationTopic(t: SimTrainer, rng: Rng): ChatTopic {
  const weights: Record<Exclude<ChatTopic, 'pottering'>, number> = {
    'show-off': 3,
    admire: 3,
    'know-how': 2,
    storyline: 2,
  };
  switch (t.playStyle) {
    case 'breeder':
      weights['know-how'] += 3;
      break;
    case 'battler':
      weights['show-off'] += 3;
      break;
    case 'collector':
      weights['show-off'] += 2;
      weights.admire += 1;
      break;
    case 'explorer':
      weights['know-how'] += 2;
      weights.storyline += 1;
      break;
    case 'socializer':
      weights.admire += 3;
      weights.storyline += 1;
      break;
  }
  const entries = Object.entries(weights) as [Exclude<ChatTopic, 'pottering'>, number][];
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let roll = rng.int(total);
  for (const [topic, w] of entries) {
    if (roll < w) return topic;
    roll -= w;
  }
  return 'show-off';
}

/** Advance one trainer through one in-game day, emitting any notable beats. */
function tickTrainerDay(t: SimTrainer, ctx: DayContext): void {
  const { rng, speciesIdx, log } = ctx;
  const channel = channelFor(t);

  // 1) Gentle whole-team growth.
  for (const mon of t.team) {
    const species = speciesIdx.get(mon.speciesId);
    if (!species) continue;
    const nature = getNature(mon.natureId);
    const { leveled, toLevel } = dayLevelGain(mon, species, nature, rng);

    if (leveled && (toLevel % 5 === 0 || rng.chance(0.25))) {
      log.push({
        kind: 'levelup',
        priority: 'ambient',
        subjectTrainerId: t.id,
        channel,
        data: {
          speciesId: mon.speciesId,
          level: toLevel,
          // Peacocking flavor: a level milestone is something to be fondly proud of.
          pride: rng.pick(PRIDE_REASONS),
          warm: true,
        },
      });
    }

    // 2) Evolution (juicy highlight).
    const evo = tryEvolve(mon, species, speciesIdx);
    if (evo) {
      const fromSpeciesId = mon.speciesId;
      Object.assign(mon, evo.evolved);
      log.push({
        kind: 'evolution',
        priority: 'highlight',
        subjectTrainerId: t.id,
        channel,
        data: { fromSpeciesId, speciesId: evo.toSpeciesId },
      });
    }
  }

  // 3) A trainer sometimes catches something new (collectors/explorers more often).
  const catchChance = t.playStyle === 'collector' ? 0.35 : t.playStyle === 'explorer' ? 0.28 : 0.15;
  if (t.team.length < 6 && rng.chance(catchChance)) {
    const sp = rng.pick(speciesIdx.all());
    const level = 3 + rng.int(8);
    const caught = makeInstance(rng, sp, level, { id: t.id, name: t.username });
    t.team.push(caught);
    log.push({
      kind: 'catch',
      priority: 'normal',
      subjectTrainerId: t.id,
      channel,
      // A fresh catch is prime peacocking material — and a hook for others to admire.
      data: { speciesId: sp.id, level, pride: rng.pick(PRIDE_REASONS), warm: true },
    });
  }

  // 4) Warm social beats among trainers (gentle, NPC-only drama) + with the player.
  emitSocial(t, ctx, channel);
}

/** Warm social activity: friendly battles, trades, guild life, greetings, praise. */
function emitSocial(t: SimTrainer, ctx: DayContext, channel: ChatChannel): void {
  const { rng, log, trainers } = ctx;

  // Befriend a peer (a friendly spar or a trade among NPCs). Partners are other
  // trainers only; affinity moves warmly. Drama (rivalry) stays mutual + mild.
  if (trainers.length > 1 && rng.chance(0.4)) {
    let other = rng.pick(trainers);
    if (other.id === t.id) other = trainers[(trainers.indexOf(other) + 1) % trainers.length]!;
    if (other.id !== t.id) {
      const sparring = rng.chance(0.6);
      adjustRelationship(t, other.id, 4 + rng.int(4), 'friend');
      remember(
        t,
        {
          aboutId: other.id,
          kind: sparring ? 'battled' : 'traded',
          summary: sparring
            ? `had a friendly spar with ${other.username}`
            : `traded with ${other.username}`,
          at: EPOCH,
        },
        `${t.id}-${other.id}-${ctx.dayIndex}`,
      );
      log.push({
        kind: sparring ? 'friendly-battle' : 'trade-done',
        priority: 'ambient',
        subjectTrainerId: t.id,
        involvedTrainerIds: [other.id],
        channel,
        data: { withTrainerId: other.id, friendly: true },
      });
      // After meeting up, warmly admire the peer's mon (mutual flattery, the
      // natural follow-on to a spar/trade — fond bragging, never one-upmanship).
      if (rng.chance(0.7)) pushAdmire(t, other, rng, ctx, channel);
    }
  }

  // Rich ambient texture so the world reads as genuinely conversational rather
  // than a wall of greetings (D14). Chattier trainers carry more of it.
  if (rng.chance(0.45 + 0.4 * t.personality.chattiness)) {
    const topic = pickConversationTopic(t, rng);
    switch (topic) {
      case 'show-off':
        pushShowOff(t, rng, ctx, channel);
        break;
      case 'know-how':
        pushKnowHow(t, rng, ctx, channel);
        break;
      case 'storyline':
        pushStoryline(t, rng, ctx, channel);
        break;
      case 'admire': {
        if (trainers.length > 1) {
          let peer = rng.pick(trainers);
          if (peer.id === t.id) peer = trainers[(trainers.indexOf(peer) + 1) % trainers.length]!;
          if (peer.id !== t.id) pushAdmire(t, peer, rng, ctx, channel);
        }
        break;
      }
    }
  }

  // A regular sometimes warmly acknowledges the player by name, or grows fonder —
  // and TWO-WAY: half the time they ask about the player's own team, inviting the
  // player to show off / answer back (D14), rather than only talking about theirs.
  if (t.lod === 'regular' && rng.chance(0.3)) {
    t.friendshipWithPlayer = clamp(
      t.friendshipWithPlayer + 1 + rng.int(3),
      FRIENDSHIP_MIN,
      FRIENDSHIP_MAX,
    );
    adjustRelationship(t, 'player', 2 + rng.int(3), 'friend');
    const praise = rng.chance(0.5);
    const asksBack = rng.chance(0.5);
    log.push({
      kind: praise ? 'player-praise' : 'greeting',
      priority: 'normal',
      subjectTrainerId: t.id,
      involvedTrainerIds: ['player'],
      channel,
      data: {
        warm: true,
        // Two-way hook: a genuine, curious question about the player's Pokémon.
        ...(asksBack ? { askAboutPlayerTeam: true, question: rng.pick(TWO_WAY_QUESTIONS) } : {}),
      },
    });
    remember(
      t,
      {
        aboutId: 'player',
        kind: praise ? 'chatted' : 'greeted',
        summary: praise ? 'cheered the player on' : 'welcomed the player warmly',
        at: EPOCH,
      },
      `${t.id}-player-${ctx.dayIndex}`,
    );
  }

  // Guild members occasionally make progress on a cooperative goal (shared purpose,
  // never an obligation).
  if (t.guildId && rng.chance(0.18)) {
    log.push({
      kind: 'guild',
      priority: 'normal',
      subjectTrainerId: t.id,
      channel: 'guild',
      data: { guildId: t.guildId, progress: true },
    });
  }
}

/** A town festival — a warm community event that draws a crowd (rule #8). */
const FESTIVAL_THEMES: readonly string[] = [
  'berry-bake-off',
  'lantern-walk',
  'friendly-battle-fair',
  'starter-reunion',
  'shiny-showcase',
];

function maybeEmitFestival(ctx: DayContext): void {
  const { rng, log, trainers } = ctx;
  if (trainers.length === 0) return;
  if (!rng.chance(0.25)) return;
  const host = rng.pick(trainers);
  log.push({
    kind: 'festival',
    priority: 'highlight',
    subjectTrainerId: host.id,
    channel: 'global',
    // A themed event gives the voice something specific + warm to organize around.
    data: { date: ctx.toDate, day: ctx.dayIndex, theme: rng.pick(FESTIVAL_THEMES), warm: true },
  });
}

// ── The tick ───────────────────────────────────────────────────────────────────

function makeRunner(speciesList: readonly SpeciesData[]): SocietyTickFn {
  const speciesIdx = indexSpecies(speciesList);

  return (input: SocietyTickInput): SocietyTickOutput => {
    const days = clamp(Math.trunc(input.elapsedDays), 0, MAX_TICK_DAYS);
    const trainers = cloneTrainers(input.trainers);

    if (days <= 0) {
      return { trainers, beats: [] };
    }

    const log = new BeatLog(EPOCH);

    for (let day = 0; day < days; day++) {
      // One fresh, fully-deterministic sub-stream per (seed, date, day). Independent
      // of roster size so adding trainers later never reshuffles earlier rolls.
      const daySeed = hash(input.worldSeed, input.toDate, `society|day|${day}`);
      const dayRng = makeRng(daySeed);

      const ctx: DayContext = {
        rng: dayRng,
        speciesIdx,
        log,
        trainers,
        dayIndex: day,
        toDate: input.toDate,
      };

      for (const t of trainers) {
        // Per-trainer sub-stream keyed by id so each trainer's day is independent
        // and reproducible regardless of iteration cost elsewhere.
        const tRng = makeRng(hashString(`${daySeed >>> 0}|${t.id}`) ^ (input.worldSeed >>> 0));
        tickTrainerDay(t, { ...ctx, rng: tRng });
      }

      maybeEmitFestival({ ...ctx, rng: makeRng(hashString(`${daySeed}|festival`)) });
    }

    return { trainers, beats: log.all() };
  };
}

/** Wire the tick to a specific species list (production passes the generated set). */
export function makeOvernightTick(species: readonly SpeciesData[]): SocietyTickFn {
  return makeRunner(species);
}

/** The default-wired overnight tick (hermetic fixtures; satisfies SocietyTickFn). */
export const runOvernightTick: SocietyTickFn = makeRunner(FIXTURE_SPECIES);

// ── Live ambient beats (present-moment chatter at a node) ────────────────────────

/**
 * Produce the "live" ambient beats for trainers currently present at `nodeId`
 * (their home zone). Deterministic given (trainers, nodeId). Warm chitchat only.
 *
 * Each present trainer gets a *distinct* topic (peacocking / admiring a neighbor /
 * sharing know-how / a quiet storyline) — never the same canned line twice — so the
 * town reads as genuinely conversational, the heart of D14. The bland "pottering"
 * line is now only a rare fallback, not the default.
 */
export function liveBeats(trainers: readonly SimTrainer[], nodeId: string): readonly Beat[] {
  const present = trainers.filter((t) => t.homeNodeId === nodeId);
  if (present.length === 0) return [];

  const log = new BeatLog(EPOCH);
  for (const t of present) {
    const rng = makeRng(hashString(`live|${nodeId}|${t.id}`));
    if (!rng.chance(t.personality.chattiness)) continue;

    // Mostly rich material; a small chance of a calm "just pottering" beat so the
    // town breathes (but never the SAME pottering line for everyone — it carries
    // the speaker's own mon/style so the voice phrases it distinctly).
    const ctx: DayContext = {
      rng,
      // liveBeats has no species index; the rich beat builders never need it.
      speciesIdx: { get: () => undefined, all: () => [] },
      log,
      trainers: present,
      dayIndex: 0,
      toDate: nodeId,
    };

    if (rng.chance(0.85)) {
      const topic = pickConversationTopic(t, rng);
      switch (topic) {
        case 'show-off':
          pushShowOff(t, rng, ctx, 'zone');
          break;
        case 'know-how':
          pushKnowHow(t, rng, ctx, 'zone');
          break;
        case 'storyline':
          pushStoryline(t, rng, ctx, 'zone');
          break;
        case 'admire': {
          let peer = rng.pick(present);
          if (peer.id === t.id && present.length > 1) {
            peer = present[(present.indexOf(peer) + 1) % present.length]!;
          }
          if (peer.id !== t.id) pushAdmire(t, peer, rng, ctx, 'zone');
          else pushShowOff(t, rng, ctx, 'zone');
          break;
        }
        default:
          pushShowOff(t, rng, ctx, 'zone');
      }
    } else {
      const star = proudestMon(t);
      log.push({
        kind: 'chitchat',
        priority: 'ambient',
        subjectTrainerId: t.id,
        channel: 'zone',
        data: {
          topic: 'pottering',
          nodeId,
          ...(star ? { speciesId: star.speciesId } : {}),
          mood: rng.pick(['content', 'sleepy', 'cheery', 'wistful']),
          warm: true,
        },
      });
    }
  }
  return log.all();
}

// ── Player-driven & two-way beats (D14) ──────────────────────────────────────

/**
 * The player's "show off a Pokémon" action (D14): turn a chosen team member into a
 * ground-truth beat the chat-director feeds nearby NPCs, who then react with warm
 * admiration. Pure: the beat's content is fully determined by the mon + an
 * `at` timestamp the caller supplies (so it stays deterministic/testable).
 *
 * Warm by construction: this is the player peacocking and being admired — there is
 * no comparison, no scoring, nothing that could make the player feel behind.
 */
export function showOffBeat(
  mon: PokemonInstance,
  at: number = EPOCH,
  channel: ChatChannel = 'zone',
): Beat {
  return {
    id: `show-off-${mon.id}`,
    kind: 'chitchat',
    priority: 'normal',
    subjectTrainerId: 'player',
    channel,
    data: {
      topic: 'show-off',
      byPlayer: true,
      speciesId: mon.speciesId,
      level: mon.level,
      ...(mon.nickname ? { nickname: mon.nickname } : {}),
      ...(mon.shiny ? { shiny: true } : {}),
      warm: true,
    },
    createdAt: at,
  };
}

/**
 * The player's "cast a move" action (mirrors {@link showOffBeat}): the player makes
 * a team Pokémon flourish a move in the tavern, which becomes a ground-truth beat the
 * chat-director feeds nearby NPCs, who react with warm, impressed admiration grounded
 * in the *specific* move + its type ("Whoa, a Flamethrower indoors?!"). Pure: the
 * beat is fully determined by the move + an `at` timestamp the caller supplies.
 *
 * Warm by construction: this is the player putting on a show and being cheered — no
 * comparison, no scoring, nothing that could make the player feel behind. `moveType`
 * lets the voice flavor reactions by element while never inventing canon.
 */
export function castBeat(
  move: { readonly id: string; readonly type: string; readonly name?: string },
  at: number = EPOCH,
  channel: ChatChannel = 'zone',
): Beat {
  return {
    id: `cast-${move.id}-${at}`,
    kind: 'chitchat',
    priority: 'normal',
    subjectTrainerId: 'player',
    involvedTrainerIds: ['player'],
    channel,
    data: {
      topic: 'cast',
      byPlayer: true,
      moveId: move.id,
      moveType: move.type,
      ...(move.name ? { moveName: move.name } : {}),
      warm: true,
    },
    createdAt: at,
  };
}

/**
 * Two-way material: warm NPC reactions to the PLAYER's actual team. Given the
 * player's mons and the trainers present, produce admiration + curious questions
 * grounded in real species the player owns — so the world talks WITH the player
 * about their Pokémon, not only about its own. Deterministic from (team, present,
 * nodeId). Strictly warm: admiration + genuine curiosity, never one-upmanship.
 */
export function playerTeamReactionBeats(
  playerTeam: readonly PokemonInstance[],
  present: readonly SimTrainer[],
  nodeId: string,
): readonly Beat[] {
  if (playerTeam.length === 0 || present.length === 0) return [];

  const log = new BeatLog(EPOCH);
  // Only regulars (the named cast) react in detail, so it stays intimate not spammy.
  const reactors = present.filter((t) => t.homeNodeId === nodeId && t.lod === 'regular');
  for (const t of reactors) {
    const rng = makeRng(hashString(`react|${nodeId}|${t.id}|${playerTeam.length}`));
    if (!rng.chance(0.6 + 0.4 * t.personality.warmth)) continue;
    const mon = rng.pick(playerTeam);
    const asking = rng.chance(0.5);
    log.push({
      kind: 'player-praise',
      priority: 'normal',
      subjectTrainerId: t.id,
      involvedTrainerIds: ['player'],
      channel: 'zone',
      data: {
        aboutPlayerMon: true,
        speciesId: mon.speciesId,
        ...(mon.nickname ? { nickname: mon.nickname } : {}),
        // Either admire it, or ask the player about it (two-way invitation).
        ...(asking
          ? { question: rng.pick(TWO_WAY_QUESTIONS) }
          : { compliment: rng.pick(ADMIRE_REASONS) }),
        warm: true,
      },
    });
  }
  return log.all();
}
